// Calil 統合検索用の Zustand ストア
//
// 検索・所蔵館マッピング・選択状態・ISBN 二次検索の enrich を管理する。
// Calil の /search は初回応答で一部の書誌しか返さない (running=true) ため、
// running=false になるまで /polling を繰り返し全件揃える。所蔵館の正確な情報は
// enrichBookByIsbn 内で ISBN 指定の apiStart→apiPoll で取得する。

import { create } from 'zustand'

import {
  getCalilMapping as apiGetMapping,
  pollCalilSearch as apiPoll,
  startCalilSearch as apiStart,
} from '@/api/services/calil'
import type { CalilBook, CalilBookUpdate, CalilRegion, CalilSearchStartResponse } from '@/api/types/calil'

import { useLibraryStatsStore } from './useLibraryStatsStore'

type SearchStatus = 'idle' | 'loading' | 'polling' | 'done' | 'error'

type LibrarySearchState = {
  region: CalilRegion
  mapping: Record<string, string>
  mappingLoaded: boolean

  query: string
  status: SearchStatus
  error: string | null

  books: CalilBook[]
  uuid: string | null
  version: number
  /** 未完了の館名リスト (実 API 仕様に合わせて配列) */
  remains: string[]

  // 展開中の本の ID 一覧 (books[].id)。複数同時に展開可能。
  expandedBookIds: string[]

  // 直近の検索セッション ID。古いポーリング結果を破棄するために使う。
  sessionId: number

  // ISBN 二次検索キャッシュ (キーは正規化 ISBN)。カード展開時に取得した所蔵情報を保持する。
  enrichedByIsbn: Record<string, CalilBook>
  // 二次検索中の ISBN (キーは正規化 ISBN)。重複リクエスト防止用。
  enrichingIsbns: Record<string, boolean>
  // 二次検索を 1 度以上試行した ISBN (キーは正規化 ISBN)。
  // 成否に関わらず finally でセットされるため、OPAC スクレイピングを
  // 「enrichment 完了後に 1 回だけ実行」する gate 判定に使う。
  enrichAttemptedIsbns: Record<string, boolean>

  setRegion: (region: CalilRegion) => void
  setQuery: (query: string) => void
  loadMapping: () => Promise<void>
  startSearch: (query: string) => Promise<{ sessionId: number; response: CalilSearchStartResponse } | null>
  /** 指定した本の展開状態をトグルする。複数同時展開可。 */
  toggleBookExpansion: (bookId: string) => void
  reset: () => void
  enrichBookByIsbn: (isbn: string) => Promise<void>
}

/** ISBN 正規化: ハイフン・空白を除去して比較・キーとして使う。 */
export const normalizeIsbn = (isbn: string | undefined | null): string | null => {
  if (!isbn) return null
  const cleaned = isbn.replace(/[-\s]/g, '')
  if (!/^\d{9}[\dxX]$|^\d{13}$/.test(cleaned)) return null
  return cleaned.toUpperCase()
}

const uniqueNumbers = (values: (number | undefined)[]): number[] => {
  const set = new Set<number>()
  for (const v of values) {
    if (typeof v === 'number' && !Number.isNaN(v)) set.add(v)
  }
  return Array.from(set)
}

const mergeBookPatch = (existing: CalilBook, patch: CalilBookUpdate): CalilBook => {
  const { _idx: _ignored, holdings, url, estimated_holdings, ...rest } = patch
  void _ignored

  const mergedHoldings = holdings ? uniqueNumbers([...existing.holdings, ...holdings]) : existing.holdings
  const mergedEstimated = estimated_holdings
    ? uniqueNumbers([...(existing.estimated_holdings ?? []), ...estimated_holdings])
    : existing.estimated_holdings
  const mergedUrl = url ? { ...(existing.url ?? {}), ...url } : existing.url

  // rest には不明なフィールドも含まれるので一応マージ (埋め上げはスプレッドで)
  return {
    ...existing,
    ...(rest as Partial<CalilBook>),
    holdings: mergedHoldings,
    estimated_holdings: mergedEstimated,
    url: mergedUrl,
  }
}

const applyDiffToBooks = (
  current: CalilBook[],
  insert: CalilBook[] | undefined,
  update: CalilBookUpdate[] | undefined,
): CalilBook[] => {
  const next = current.slice()
  if (update) {
    for (const patch of update) {
      const idx = patch._idx
      if (idx < 0 || idx >= next.length) continue
      const existing = next.at(idx)
      if (!existing) continue
      next.splice(idx, 1, mergeBookPatch(existing, patch))
    }
  }
  if (insert) {
    for (const book of insert) {
      next.push(book)
    }
  }
  return next
}

export const useLibrarySearchStore = create<LibrarySearchState>((set, get) => ({
  region: 'saitama',
  mapping: {},
  mappingLoaded: false,

  query: '',
  status: 'idle',
  error: null,

  books: [],
  uuid: null,
  version: 0,
  remains: [],

  expandedBookIds: [],
  sessionId: 0,

  enrichedByIsbn: {},
  enrichingIsbns: {},
  enrichAttemptedIsbns: {},

  // region 変更時は mapping キャッシュを破棄する。将来的に region が可変になった場合、
  // 古い地域のマッピングが混ざるのを防ぐ。
  // ISBN 二次検索キャッシュも region に紐づく (図書館 ID が region 依存) ため合わせて破棄する。
  // OPAC 統計キャッシュも URL が region の図書館に紐づくため破棄する。
  setRegion: (region) => {
    useLibraryStatsStore.getState().reset()
    set({
      region,
      mapping: {},
      mappingLoaded: false,
      enrichedByIsbn: {},
      enrichingIsbns: {},
      enrichAttemptedIsbns: {},
    })
  },
  setQuery: (query) => set({ query }),

  loadMapping: async () => {
    if (get().mappingLoaded) return
    try {
      const res = await apiGetMapping(get().region)
      set({ mapping: res.libraries ?? {}, mappingLoaded: true })
    } catch (error) {
      // 失敗時は mappingLoaded を立てず、次回呼び出しでリトライ可能にする。
      console.error('Failed to load Calil mapping:', error)
    }
  },

  startSearch: async (query) => {
    const trimmed = query.trim()
    if (!trimmed) return null

    const nextSessionId = get().sessionId + 1
    set({
      query: trimmed,
      status: 'loading',
      error: null,
      books: [],
      uuid: null,
      version: 0,
      remains: [],
      expandedBookIds: [],
      sessionId: nextSessionId,
    })

    try {
      const res = await apiStart({ region: get().region, free: trimmed })
      // 検索セッションが既に上書きされていたら破棄
      if (get().sessionId !== nextSessionId) return null

      let books: CalilBook[] = res.books ?? []
      let currentVersion = res.version
      let running = res.running

      // 初回応答を先に反映 (running=true でもカードリストを見せる)
      set({
        uuid: res.uuid,
        version: currentVersion,
        remains: res.remains ?? [],
        books,
        status: running ? 'polling' : 'done',
      })

      // running=false になるまで /polling を繰り返し、すべての書誌を揃える。
      // Calil は検索館の応答を待ちながら books_diff で逐次返す仕様。
      const MAX_ITER = 20
      for (let i = 0; running && i < MAX_ITER; i += 1) {
        const pollRes = await apiPoll(res.uuid, currentVersion, { diff: 1, timeout: 10 })
        // 検索セッションが入れ替わったら回送内容は破棄
        if (get().sessionId !== nextSessionId) return null

        currentVersion = pollRes.version
        running = pollRes.running
        books = applyDiffToBooks(books, pollRes.books_diff?.insert, pollRes.books_diff?.update)

        set({
          version: currentVersion,
          remains: pollRes.remains ?? [],
          books,
          status: running ? 'polling' : 'done',
        })
      }

      // MAX_ITER に到達しても running=true の場合はここで打ち切って done にする
      if (running) set({ status: 'done' })

      return { sessionId: nextSessionId, response: res }
    } catch (error) {
      if (get().sessionId !== nextSessionId) return null
      const message = error instanceof Error ? error.message : String(error)
      set({ status: 'error', error: message })
      return null
    }
  },

  toggleBookExpansion: (bookId) => {
    const current = get().expandedBookIds
    const next = current.includes(bookId) ? current.filter((id) => id !== bookId) : [...current, bookId]
    set({ expandedBookIds: next })
  },

  reset: () => {
    useLibraryStatsStore.getState().reset()
    set({
      query: '',
      status: 'idle',
      error: null,
      books: [],
      uuid: null,
      version: 0,
      remains: [],
      expandedBookIds: [],
      sessionId: get().sessionId + 1,
      enrichedByIsbn: {},
      enrichingIsbns: {},
      enrichAttemptedIsbns: {},
    })
  },

  enrichBookByIsbn: async (rawIsbn) => {
    const isbn = normalizeIsbn(rawIsbn)
    if (!isbn) return
    const { enrichedByIsbn, enrichingIsbns, region } = get()
    if (enrichedByIsbn[isbn]) return
    if (enrichingIsbns[isbn]) return

    set({ enrichingIsbns: { ...get().enrichingIsbns, [isbn]: true } })

    try {
      const startRes = await apiStart({ region, isbn })
      let currentVersion = startRes.version
      let books: CalilBook[] = startRes.books ?? []
      let running = startRes.running

      // running=false になるまで /polling を叩く (最大 20 回で強制打ち切り)
      const MAX_ITER = 20
      for (let i = 0; running && i < MAX_ITER; i += 1) {
        const pollRes = await apiPoll(startRes.uuid, currentVersion, { diff: 1, timeout: 10 })
        currentVersion = pollRes.version
        running = pollRes.running
        books = applyDiffToBooks(books, pollRes.books_diff?.insert, pollRes.books_diff?.update)
      }

      // ISBN 検索は基本 1 冊しか返さない。books[0] を採用する。
      const first = books.at(0)
      if (!first) {
        debugWarn('[calil] ISBN enrichment returned no book', { isbn })
        return
      }

      set({
        enrichedByIsbn: { ...get().enrichedByIsbn, [isbn]: first },
      })
    } catch (error) {
      debugWarn('[calil] ISBN enrichment failed', {
        isbn,
        message: error instanceof Error ? error.message : String(error),
      })
    } finally {
      const nextEnriching = { ...get().enrichingIsbns }
      delete nextEnriching[isbn]
      set({
        enrichingIsbns: nextEnriching,
        enrichAttemptedIsbns: { ...get().enrichAttemptedIsbns, [isbn]: true },
      })
    }
  },
}))

const debugWarn = (...args: unknown[]): void => {
  if (import.meta.env.DEV) console.warn(...args)
}
